import { MusicIcon } from '../components/icons/MusicIcon';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';
import { usePremium } from '../hooks/usePremium';
import { Music, Globe, Lock, Users, Zap, Clock, Trophy, Loader2, Crown, User, Link as LinkIcon } from 'lucide-react';
import GameModeSelector from '../components/GameModeSelector';
import { GAME_MODES, GameModeId } from '../../../shared/constants';

// Version 2.1 - Playlists Spotify utilisateur

interface SpotifyPlaylist {
  id: string;
  name: string;
  images: { url: string }[];
  tracks: { total: number };
  owner: { display_name: string };
}

export default function CreateRoomPage() {
  const navigate = useNavigate();
  const { socket, isConnected, createRoom, sessionError } = useSocketStore();
  const { player, spotifyToken, spotifyExpiresAt, refreshSpotifyToken } = useAuthStore();
  
  const [roomName, setRoomName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [roundsCount, setRoundsCount] = useState(10);
  const [roundDuration, setRoundDuration] = useState(30);
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<GameModeId>('classic');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (sessionError) { setError(sessionError); setIsCreating(false); } }, [sessionError]);
  
  // Playlists de l'utilisateur
  const [userPlaylists, setUserPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  
  // Premium - utilise le hook pour récupérer le vrai statut
  const { isPremium } = usePremium();
  
  // Charger les playlists de l'utilisateur
  useEffect(() => {
    console.log('🎵 CreateRoomPage - spotifyToken:', spotifyToken ? 'présent' : 'absent');
    console.log('🎵 CreateRoomPage - spotifyExpiresAt:', spotifyExpiresAt, 'now:', Date.now());
    
    if (!spotifyToken) {
      console.log('⚠️ Pas de token Spotify, impossible de charger les playlists');
      return;
    }
    
    // Vérifier si le token est expiré
    if (spotifyExpiresAt && Date.now() > spotifyExpiresAt - 60000) {
      console.log('🔄 Token expiré, rafraîchissement avant chargement...');
      refreshSpotifyToken();
      return; // Le useEffect se relancera quand spotifyToken changera
    }
    
    const fetchPlaylists = async () => {
      setLoadingPlaylists(true);
      console.log('📡 Chargement des playlists Spotify...');
      try {
        const response = await fetch('/api/playlists', {
          headers: {
            'Authorization': `Bearer ${spotifyToken}`,
          },
        });
        
        console.log('📡 Réponse playlists:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Playlists reçues:', data.playlists?.length || 0);
          setUserPlaylists(data.playlists || []);
          // Sélectionner la première playlist par défaut
          if (data.playlists?.length > 0) {
            setSelectedPlaylist(data.playlists[0].id);
          }
        } else if (response.status === 401) {
          console.log('🔄 Token rejeté par l\'API, rafraîchissement...');
          refreshSpotifyToken();
        } else {
          console.error('❌ Erreur API playlists:', response.status);
        }
      } catch (err) {
        console.error('❌ Erreur chargement playlists:', err);
      } finally {
        setLoadingPlaylists(false);
      }
    };
    
    fetchPlaylists();
  }, [spotifyToken, spotifyExpiresAt, refreshSpotifyToken]);
  
  // Écouter la création de room réussie
  useEffect(() => {
    if (!socket) return;
    
    const handleRoomCreated = ({ room }: { room: { code: string } }) => {
      setIsCreating(false);
      navigate(`/room/${room.code}`);
    };
    
    const handleRoomError = ({ message }: { message: string }) => {
      setIsCreating(false);
      setError(message);
    };
    
    socket.on('room:created', handleRoomCreated);
    socket.on('room:error', handleRoomError);
    
    return () => {
      socket.off('room:created', handleRoomCreated);
      socket.off('room:error', handleRoomError);
    };
  }, [socket, navigate]);
  
  const handleCreate = () => {
    if (!socket || !isConnected) {
      setError('Non connecté au serveur');
      return;
    }
    
    if (!player) {
      setError('Vous devez être connecté');
      return;
    }
    
    if (selectedMode !== 'deaf' && !selectedPlaylist) {
      setError('Veuillez sélectionner une playlist');
      return;
    }
    
    // Vérifier les limites premium
    if (!isPremium && !isPublic) {
      setError('Les rooms privées nécessitent un compte Premium');
      return;
    }
    
    if (!isPremium && roundsCount > 20) {
      setError('Plus de 20 manches nécessite un compte Premium');
      return;
    }
    
    if (!isPremium && roundDuration !== 30) {
      setError('La durée personnalisée nécessite un compte Premium');
      return;
    }
    
    // Vérifier si le mode de jeu sélectionné est premium
    const selectedModeInfo = GAME_MODES[selectedMode];
    if (!isPremium && selectedModeInfo.isPremium) {
      setError(`Le mode "${selectedModeInfo.name}" nécessite un compte Premium`);
      return;
    }
    
    setIsCreating(true);
    setError(null);
    
    const settings = {
      name: roomName || `Room de ${player.name}`,
      playlistId: selectedMode === 'deaf' ? 'deaf-test' : selectedPlaylist!,
      isPrivate: !isPublic,
      settings: {
        mode: selectedMode,
        maxPlayers,
        roundCount: roundsCount,
        roundDuration,
      },
    };
    
    createRoom(settings.name, settings.playlistId, settings.settings, settings.isPrivate);
  };
  
  // Si l'utilisateur n'est pas connecté
  if (!player) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <User className="w-16 h-16 mx-auto mb-4 text-primary-400" />
        <h1 className="text-2xl font-bold mb-4">Connexion requise</h1>
        <p className="text-dark-400 mb-6">
          Vous devez être connecté pour créer une room.
        </p>
        <Link to="/" className="btn-primary inline-block">
          Retour à l'accueil
        </Link>
      </div>
    );
  }
  
  // Si pas de token Spotify lié
  if (!spotifyToken) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <LinkIcon className="w-16 h-16 mx-auto mb-4 text-green-400" />
        <h1 className="text-2xl font-bold mb-4">Liez votre compte Spotify</h1>
        <p className="text-dark-400 mb-6">
          Pour créer une room avec vos playlists, vous devez lier votre compte Spotify depuis votre profil.
        </p>
        <Link to="/profile" className="btn-success inline-flex items-center gap-2">
          <Music className="w-5 h-5" />
          Aller à mon profil
        </Link>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-display font-bold text-center mb-8">
        <Zap className="inline-block w-8 h-8 mr-2 text-primary-400" />
        Créer une Room
      </h1>
      
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}
      
      <div className="space-y-6">
        {/* Nom de la room */}
        <div className="card">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Nom de la Room
          </label>
          <input
            type="text"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder={`Room de ${player?.name || 'Joueur'}`}
            className="input w-full"
            maxLength={30}
          />
        </div>
        
        {/* Visibilité */}
        <div className="card">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Visibilité
          </label>
          <div className="flex gap-4">
            <button
              onClick={() => setIsPublic(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all ${
                isPublic
                  ? 'border-primary-500 bg-primary-500/20 text-primary-300'
                  : 'border-dark-600 bg-dark-700 text-gray-400 hover:border-dark-500'
              }`}
            >
              <Globe className="w-5 h-5" />
              Publique
            </button>
            <button
              onClick={() => setIsPublic(false)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all ${
                !isPublic
                  ? 'border-primary-500 bg-primary-500/20 text-primary-300'
                  : 'border-dark-600 bg-dark-700 text-gray-400 hover:border-dark-500'
              }`}
            >
              <Lock className="w-5 h-5" />
              Privée {!isPremium && <Crown className="w-4 h-4 text-yellow-400" />}
            </button>
          </div>
          {!isPublic && !isPremium && (
            <p className="text-xs text-yellow-400 mt-2">
              <MusicIcon name="diamond"/> Les rooms privées sont réservées aux membres Premium
            </p>
          )}
        </div>
        
        {/* Mode de jeu */}
        <div className="card">
          <GameModeSelector
            selectedMode={selectedMode}
            onSelectMode={setSelectedMode}
            isPremium={isPremium}
          />
        </div>
        
        {/* Paramètres de jeu */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Paramètres de jeu</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Max joueurs */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <Users className="w-4 h-4" />
                Joueurs max
              </label>
              <select
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(Number(e.target.value))}
                className="input w-full"
              >
                {isPremium ? (
                  // Premium : toutes les options
                  [4, 6, 8, 10, 12, 16, 20, 30, 50, 75, 100].map((n) => (
                    <option key={n} value={n}>{n} joueurs</option>
                  ))
                ) : (
                  // Gratuit : max 4 joueurs
                  [2, 3, 4].map((n) => (
                    <option key={n} value={n}>{n} joueurs</option>
                  ))
                )}
              </select>
              {!isPremium && (
                <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Premium : jusqu'à 100 joueurs
                </p>
              )}
            </div>
            
            {/* Nombre de manches */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <Trophy className="w-4 h-4" />
                Manches
              </label>
              <select
                value={roundsCount}
                onChange={(e) => setRoundsCount(Number(e.target.value))}
                className="input w-full"
              >
                {isPremium ? (
                  // Premium : toutes les options
                  [5, 10, 15, 20, 25, 30, 40, 50, 60, 80, 100].map((n) => (
                    <option key={n} value={n}>{n} manches</option>
                  ))
                ) : (
                  // Gratuit : 10 à 20 manches
                  [10, 12, 15, 18, 20].map((n) => (
                    <option key={n} value={n}>{n} manches</option>
                  ))
                )}
              </select>
              {!isPremium && (
                <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Premium : jusqu'à 100 manches
                </p>
              )}
            </div>
            
            {/* Durée par manche */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <Clock className="w-4 h-4" />
                Durée
              </label>
              {isPremium ? (
                <select
                  value={roundDuration}
                  onChange={(e) => setRoundDuration(Number(e.target.value))}
                  className="input w-full"
                >
                  {[15, 20, 25, 30, 45, 60, 90, 120].map((n) => (
                    <option key={n} value={n}>{n} sec</option>
                  ))}
                </select>
              ) : (
                <div className="input w-full bg-dark-700 text-gray-400 cursor-not-allowed flex items-center justify-between">
                  <span>30 sec</span>
                  <Lock className="w-4 h-4 text-yellow-400" />
                </div>
              )}
              {!isPremium && (
                <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Premium : durée personnalisable
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Playlists Spotify — hidden in deaf mode */}
        {selectedMode === 'deaf' ? (
          <div className="card text-center py-8">
            <div className="text-4xl mb-3"><MusicIcon name="film"/></div>
            <p className="text-white font-semibold mb-1">Mode Deaf Test</p>
            <p className="text-dark-400 text-sm">Les clips video sont selectionnes automatiquement depuis notre base de donnees.</p>
          </div>
        ) : (
        <div className="card">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
            <Music className="w-5 h-5 text-green-500" />
            Tes Playlists Spotify
          </label>
          
          {loadingPlaylists ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
              <span className="ml-2 text-dark-400">Chargement de tes playlists...</span>
            </div>
          ) : userPlaylists.length === 0 ? (
            <div className="text-center py-8">
              <Music className="w-12 h-12 mx-auto mb-3 text-dark-500" />
              <p className="text-dark-400">
                Aucune playlist trouvée. Crée des playlists sur Spotify !
              </p>
              <a
                href="https://open.spotify.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:underline mt-2 inline-block"
              >
                Ouvrir Spotify →
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
              {userPlaylists.map((playlist) => (
                <button
                  key={playlist.id}
                  onClick={() => setSelectedPlaylist(playlist.id)}
                  className={`relative p-2 rounded-xl border-2 text-left transition-all overflow-hidden ${
                    selectedPlaylist === playlist.id
                      ? 'border-green-500 bg-green-500/20'
                      : 'border-dark-600 bg-dark-700 hover:border-dark-500'
                  }`}
                >
                  {playlist.images?.[0]?.url ? (
                    <img
                      src={playlist.images[0].url}
                      alt={playlist.name}
                      className="w-full aspect-square object-cover rounded-lg mb-2"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-dark-600 rounded-lg mb-2 flex items-center justify-center">
                      <Music className="w-8 h-8 text-dark-400" />
                    </div>
                  )}
                  <p className="text-sm font-medium truncate">{playlist.name}</p>
                  <p className="text-xs text-dark-400">{playlist.tracks?.total || 0} titres</p>
                  
                  {selectedPlaylist === playlist.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs"><MusicIcon name="check"/></span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Promo Premium pour les non-membres */}
        {!isPremium && (
          <Link
            to="/premium"
            className="block card bg-gradient-to-r from-yellow-500/20 via-primary-500/20 to-purple-500/20 border-2 border-yellow-500/50 hover:border-yellow-400 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-yellow-300">Passe en Premium !</h3>
                <p className="text-sm text-gray-400">
                  Rooms privées, 100 manches, power-ups illimités, badges exclusifs...
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-yellow-300">5€</div>
                <div className="text-xs text-gray-500">/mois</div>
              </div>
            </div>
          </Link>
        )}
        
        {/* Bouton créer */}
        <button
          onClick={handleCreate}
          disabled={isCreating || !isConnected || !selectedPlaylist}
          className="btn-primary w-full py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreating ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Création...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Zap className="w-5 h-5" />
              Créer la Room
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
